import React, { ChangeEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { faCircleQuestion, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";

import useOnclickOutside from "hooks/useOnclickOutside/useOnclickOutside";
import useDebounce from "hooks/events/useDebounce";
import Spin from "components/spin/spin";

import "./selectNew.scss";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Panel from "components/panel/Panel";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";

export type TSelectOption<K> = {
  /** must be unique to the object. */
  id: string | number;
  label: string | number;
  extraValue?: K;
  /** add disable styling to list-item */
  disabled?: boolean;
};

type InputType = React.InputHTMLAttributes<HTMLInputElement>["type"];

type TSelectNewMainProps<K> = {
  /** Connects label to input value. */
  id: string;
  /** Hides right-side icon and their actions. */
  disabled?: boolean;
  /** Displayed strictly with searchable Select component. */
  placeholder?: string;
  /**
   * Allow clearing the value using right-icon action callback. Rendered on input hover.
   * - Single-select component clears search & returns callback.
   * - Multi-select component clears search & returns early (no callback) if search is present.
   */
  onClear?: () => void;
  /** Dropdown values to display and select from. */
  options: TSelectOption<K>[];
  /** Opt-in to manually apply UI style to children of <li> elements found in dropdown. */
  renderOption?: (option: TSelectOption<K>) => JSX.Element;
  /** Opt-in to customize the closed select display for non-searchable selects. */
  renderValue?: (option: TSelectOption<K>) => JSX.Element;
  /** Dropdown spinner to denote loading dropdown data. */
  isLoading?: boolean;
  /** Manipulate the overall dropdown size. */
  dropdownSize?: "small";
  /**
   * Generate searchbar for select dropdown values.
   * - Input of `type=text` instead of `type=button`.
   * - Searches for match against option label.
   * - number passed is wanted search debounce on user input.  Default = 1s.
   * - Single-select component onBlur sets search string to selected value if found.
   */
  searchable?: boolean | number;
  /** Render out label element for component. Placed above the Select element itself. */
  label?: string;
  /** Render out help text element for component.  Placed below the Select element itself. */
  helpText?: string;
  className?: string;
  labelHidden?: boolean;
  /** Auto focus the search input */
  autoFocus?: boolean;
  /** Callback for when the search value changes */
  onSearchCallback?: (search: string) => void;
  /**Adds icon w/ tooltip next to the label */
  tooltip?: React.ReactNode | string;
  /** Toggle for error state */
  error?: boolean;
  /** Search input type: Ex. number, text */
  searchInputType?: InputType;
  /** Mobile/Tablet panel slide up search */
  mobileSlideupSearch?: boolean;
  showAmountSelected?: boolean;
  dropdownPosition?: "bottom" | "top";
  hightLightSelected?: boolean;
  /**Render dropdown outside root DOM using <Portal> component. Set to true for dropdowns used inside modals that overflow */
  usePortal?: boolean;
  portalId?: string;
  /** Rendered inside the dropdown when a searchable filter has no matches. */
  noData?: React.ReactNode | ((context: { search: string; clearSearch: () => void }) => React.ReactNode);
};

type TSingleSelectNewProps<T = string | number, K = any> = {
  /**
   * Denotes a single-select component.
   * - Currently selected value found within dropdown.
   * - return value is infered from data type of this value.
   * @type {string | number}
   */
  value: T;
  multiValue?: never;
  onChange: (value: T, extraValue: K) => void;
};

type TMultiSelectProps<T = string | number, K = any> = {
  value?: never;
  /**
   * Denotes a multi-select component.
   * - Currently selected values found within dropdown.
   * - return value is infered from primitive data type found in array.
   * @type {string[] | number[]}
   *
   */
  multiValue: Array<T>;
  onChange: (value: T, extraValue: K) => void;
  showAmountSelected?: boolean;
};

export type TSelectNewProps<T, K> = TSelectNewMainProps<K> & (TSingleSelectNewProps<T, K> | TMultiSelectProps<T, K>);

function filterSelectOptions<K>(
  options: TSelectOption<K>[],
  debouncedSearch: string,
  searchable: boolean | number,
): TSelectOption<K>[] {
  return options.filter(option =>
    options.some(val => val.label.toString() === debouncedSearch) || debouncedSearch.length === 0 || !searchable
      ? true
      : String(option.label).toLowerCase().includes(debouncedSearch.toLowerCase()),
  );
}

/** Passing both `value` & `multiValue` prop will throw a typescript error.
 * - Single-Select functionality - use value
 * - Multi-Select functionality - use multiValue
 * */
export default function SelectNew<T extends string | number = string | number, K = any>(props: TSelectNewProps<T, K>) {
  const {
    id,
    value,
    multiValue,
    onChange,
    disabled,
    placeholder,
    searchable = false,
    onClear,
    options = [],
    renderOption = null,
    renderValue = null,
    isLoading = false,
    dropdownSize,
    helpText,
    label,
    className,
    onSearchCallback,
    tooltip,
    searchInputType,
    mobileSlideupSearch,
    showAmountSelected,
    dropdownPosition = "bottom",
    hightLightSelected,
    usePortal,
    portalId,
    noData,
  } = props;

  const { t } = useTranslation();
  const { state: panelState, updateModal: updatePanelState, closeModal: closePanelState } = useModal();
  const [selectPosition, setSelectPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const scrollableParentElementRef = useRef<HTMLElement>(null);
  const prevValueRef = useRef(value);
  /** True while the search input displays an option label rather than text typed by the user. */
  const searchIsOptionLabelRef = useRef(false);

  const updatePosition = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const top = rect.bottom;
    const left = rect.left;
    setSelectPosition({ top, left });
  }, []);

  // Single-select component sets search value to currently selected value when user clicks outside.
  const selectRef = useOnclickOutside(() => {
    setDropdownOpen(false);

    // reset search value to actual value if given
    const currentValue = options.find(val => val.id === value);
    if (currentValue) {
      if (search !== currentValue.label?.toString()) {
        searchIsOptionLabelRef.current = true;
        setSearch(currentValue.label.toString());
      }
    } else if (!mobileSlideupSearch) {
      setSearch("");
    }
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const iconRef = useRef(null);

  const [hovered, setHovered] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, typeof searchable === "number" ? searchable : 1000);
  const filteredOptions = filterSelectOptions(options, debouncedSearch, searchable);
  const showNoData =
    !!noData &&
    !isLoading &&
    !!searchable &&
    debouncedSearch.length > 0 &&
    search === debouncedSearch &&
    filteredOptions.length === 0;

  const clearSearch = useCallback(() => {
    setSearch("");
    onSearchCallback?.("");
  }, [onSearchCallback]);

  const noDataContent = showNoData ? (typeof noData === "function" ? noData({ search, clearSearch }) : noData) : null;

  /** With async options passed, ensure search string is sync'd with value */
  useEffect(() => {
    const valueChanged = prevValueRef.current !== value;
    prevValueRef.current = value;

    if (value === null) {
      // Options refreshed from an async search must not wipe what the user is typing.
      if (search.length > 0 && (valueChanged || searchIsOptionLabelRef.current)) {
        searchIsOptionLabelRef.current = false;
        setSearch("");
      }
      return;
    }

    if (options.some(val => val.id === value)) {
      const searchStart = options.find(val => val.id === value);
      searchIsOptionLabelRef.current = true;
      setSearch(searchStart.label as string);
    }
  }, [value, options]);

  useEffect(() => {
    if (props.searchable && props.autoFocus) {
      setTimeout(() => {
        inputRef.current?.focus();
        setDropdownOpen(() => true);
      }, 150);
    }
  }, []);

  useLayoutEffect(() => {
    if (!usePortal) {
      return;
    }
    if (dropdownOpen) {
      updatePosition();
    }
  }, [dropdownOpen, updatePosition, usePortal]);

  useEffect(() => {
    if (!usePortal) {
      return;
    }

    let scrollParent: HTMLElement = null;
    if (ref.current) {
      scrollParent = getOverflowParent(ref.current);
    }

    const handleScroll = () => updatePosition();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    if (scrollParent && scrollParent !== document.body && scrollParent !== document.documentElement) {
      scrollParent.addEventListener("scroll", handleScroll, { passive: true });
      scrollableParentElementRef.current = scrollParent;
    } else {
      scrollableParentElementRef.current = null;
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (scrollableParentElementRef.current) {
        scrollableParentElementRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [usePortal, updatePosition]);

  const onValueSelect = useCallback(
    (event: React.MouseEvent<HTMLLIElement, MouseEvent>, id: string | number, extraValue: K = undefined) => {
      event.stopPropagation();
      event.preventDefault();
      onChange(id as T, extraValue);

      const searchLabel = options.find(val => val.id === id)?.label;

      // single-select values
      if (typeof multiValue === "undefined") {
        if (value !== id) {
          searchIsOptionLabelRef.current = true;
          setSearch(String(searchLabel));
          setDropdownOpen(false);
        }
      }

      // multi-select values
      if (typeof value === "undefined") {
        return;
      }
    },
    [value, multiValue, options],
  );

  /** Prioritize clearing strictly search value if one is given.  Clearing without a search clears selected values. */
  const onValueClear = useCallback(() => {
    // Prioritize search clear only if present & multiSelect
    if (search.length > 0 && typeof value === "undefined") {
      clearSearch();
      return;
    }

    // clear selected values
    if (search.length > 0) {
      clearSearch();
    }

    onClear?.();
  }, [clearSearch, onClear, search, value]);

  function onSearch(e: ChangeEvent<HTMLInputElement>) {
    searchIsOptionLabelRef.current = false;
    setSearch(e.target.value);
    onSearchCallback?.(e.target.value);

    // auto-select dropdown value if matches search string ??
    // if(options.some(val => val.label.toString().toLowerCase() === e.target.value.toLowerCase())) {
    //     const selectedFromSearch = options.find(val => val.label.toString().toLowerCase() === e.target.value.toLowerCase());
    //     onChange(selectedFromSearch.id as T, selectedFromSearch.extraValue);
    // }
  }

  /**
   * @description Returns the first overflow parent HTML element found from a starting HTML Element
   * @param {HTMLElement} element - Starting HTML Element
   * @returns HTMLElement
   */
  function getOverflowParent(element: HTMLElement): HTMLElement {
    const overflowYStyling = element && window.getComputedStyle(element).overflowY;
    const scrollable = overflowYStyling !== "visible" && overflowYStyling !== "hidden";

    if (!element) {
      return null;
    } else if (scrollable && element.scrollHeight >= element.clientHeight) {
      // check if element has overflow scrollable css property and the element's contents scroll height >= the element's height
      return element;
    }

    return getOverflowParent(element.parentElement) || document.body;
  }

  const DropdownWrapper = useCallback(({ usePortal, children }: { usePortal: boolean; children: React.ReactNode }) => {
    return usePortal ? (
      <Portal parentId={portalId}>
        <div className="ui-select_element">{children}</div>
      </Portal>
    ) : (
      <>{children}</>
    );
  }, []);
  const selectedOption = options.find(val => val.id === value);

  return (
    <div className={`ui-select_element  ${className ?? ""}`} onClick={() => inputRef.current?.focus()}>
      <div className="flex justify-between">
        {label && (
          <label htmlFor={`input-element-search_${id}`}>
            {label}{" "}
            {tooltip ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="selectnew-tooltip" className="ui-select_element_tooltip">
                    <>{tooltip}</>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faCircleQuestion} />
              </OverlayTrigger>
            ) : null}
          </label>
        )}
        {showAmountSelected ? <label>{`${multiValue.length} ${t("Selected")}`}</label> : null}
      </div>
      <div
        ref={usePortal ? ref : selectRef}
        className="ui-select_content"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {searchable && (
          <span className="ui-select_left_icon">
            <FontAwesomeIcon icon={["far", "magnifying-glass"]} />
          </span>
        )}

        {searchable ? (
          <input
            id={`input-element-search_${id}`}
            ref={inputRef}
            className={`ui-input_element searchable` + (props.error ? " ui-select-error" : "")}
            type={searchInputType ? searchInputType : "text"}
            value={search}
            onChange={onSearch}
            onClick={mobileSlideupSearch ? () => updatePanelState({ isOpen: true }) : () => setDropdownOpen(() => true)}
            onFocus={() => setDropdownOpen(true)}
            disabled={disabled}
            aria-disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            readOnly={mobileSlideupSearch}
          />
        ) : renderValue ? (
          <button
            id={`input-element-btn_${id}`}
            ref={inputRef as React.RefObject<HTMLButtonElement>}
            type="button"
            className={
              `ui-input_element ui-select_custom-value${!selectedOption && placeholder ? " placeholder" : ""}` +
              (props.error ? " ui-select-error" : "") +
              (hightLightSelected && (value || multiValue?.length > 0) ? " ui-select_element-highlight-border" : "")
            }
            onClick={() => setDropdownOpen(prev => !prev)}
            disabled={disabled}
            aria-disabled={disabled}
          >
            {selectedOption ? renderValue(selectedOption) : placeholder ?? ""}
          </button>
        ) : (
          <input
            id={`input-element-btn_${id}`}
            ref={inputRef}
            className={
              `ui-input_element${!options.some(val => val.id === value) && placeholder ? " placeholder" : ""}` +
              (props.error ? " ui-select-error" : "") +
              (hightLightSelected && (value || multiValue?.length > 0) ? " ui-select_element-highlight-border" : "")
            }
            type="button"
            value={
              !options.some(val => val.id === value) ? placeholder ?? "" : options.find(val => val.id === value).label
            }
            onClick={() => setDropdownOpen(prev => !prev)}
            disabled={disabled}
            aria-disabled={disabled}
          />
        )}
        {!disabled ? (
          <span ref={iconRef} className={`ui-select_right_icon`}>
            <RightIcon
              clearable={onClear && hovered && (!!value || search.length > 0 || multiValue?.length > 0)}
              dropdownOpen={dropdownOpen}
              clearClick={onValueClear}
              carrotClick={() => setDropdownOpen(prev => (searchable ? true : !prev))}
              hideCarrots={searchable as boolean}
            />
          </span>
        ) : null}
      </div>
      <DropdownWrapper usePortal={usePortal}>
        <div
          ref={selectRef}
          className={`ui-select_dropdown ${dropdownSize ?? ""}${dropdownOpen && !mobileSlideupSearch ? " open" : ""} ${
            dropdownPosition === "top" ? "ui-select_dropdown-position-top" : ""
          }`}
          style={
            usePortal
              ? {
                  top: selectPosition.top ?? ref.current?.getBoundingClientRect().bottom ?? 0,
                  left: selectPosition.left ?? ref.current?.getBoundingClientRect().left ?? 0,
                  width: ref?.current?.clientWidth,
                  zIndex: 510,
                  maxHeight: Math.max(window.innerHeight - (ref?.current?.getBoundingClientRect().bottom ?? 0) - 8, 0),
                  minWidth: "fit-content",
                  position: "fixed",
                }
              : undefined
          }
        >
          <ul id="ui-rc-select_new_dropdown">
            {isLoading ? (
              <li className="ui-select_dropdown_item ui-select_dropdown_spinner">
                <Spin />
              </li>
            ) : showNoData ? (
              <li className="ui-select_dropdown_no-data">{noDataContent}</li>
            ) : (
              filteredOptions.map(option => {
                const chosenValue = value ? value : multiValue;
                const singleAndSelected =
                  option.id === (chosenValue as string | number) || option.label === (chosenValue as string | number);
                const multiAndSelect =
                  Array.isArray(chosenValue) &&
                  (chosenValue.some(val => val === option.id) || chosenValue.some(val => val === option.label));

                return (
                  <li
                    key={option.id}
                    onClick={e => (!option.disabled ? onValueSelect(e, option.id, option?.extraValue) : null)}
                    className={`ui-select_dropdown_item${singleAndSelected || multiAndSelect ? " selected" : ""}${
                      option.disabled ? " disabled" : ""
                    }`}
                    aria-disabled={option.disabled}
                  >
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <>
                        <p className="ui-select_dropdown_text">{option.label}</p>
                        {(singleAndSelected || multiAndSelect) && (
                          <FontAwesomeIcon className="ui-select_dropdown_check" icon={["far", "check"]} />
                        )}
                      </>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      </DropdownWrapper>
      {helpText && <span className="ui-select_help_text">{helpText}</span>}
      {mobileSlideupSearch && (
        <Portal isMounted={true}>
          <Panel
            open={mobileSlideupSearch && panelState?.isOpen}
            title={t("Search")}
            onClose={() => {
              void closePanelState();
              clearSearch();
            }}
            animate
            stacked
            pagePercent={50}
          >
            <div className="ui-select_element p-2 h-full flex flex-col">
              <div className="ui-select_content">
                <span className="ui-select_left_icon">
                  <FontAwesomeIcon icon={["far", "magnifying-glass"]} />
                </span>
                <input
                  id={`input-element-search_${id}`}
                  ref={inputRef}
                  className={`ui-input_element searchable` + (props.error ? " ui-select-error" : "")}
                  type={searchInputType ? searchInputType : "text"}
                  value={search}
                  onChange={onSearch}
                  disabled={disabled}
                  aria-disabled={disabled}
                  placeholder={placeholder}
                  autoComplete="off"
                />
              </div>
              <div className="ui-select_dropdown_item_mobile-search-container">
                <ul id="ui-rc-select_new_dropdown">
                  {isLoading ? (
                    <li className="ui-select_dropdown_item ui-select_dropdown_spinner">
                      <Spin />
                    </li>
                  ) : showNoData ? (
                    <li className="ui-select_dropdown_no-data">{noDataContent}</li>
                  ) : (
                    filteredOptions.map(option => {
                      const chosenValue = value ? value : multiValue;
                      const singleAndSelected =
                        option.id === (chosenValue as string | number) ||
                        option.label === (chosenValue as string | number);
                      const multiAndSelect =
                        Array.isArray(chosenValue) &&
                        (chosenValue.some(val => val === option.id) || chosenValue.some(val => val === option.label));

                      return (
                        <li
                          key={option.id}
                          onClick={e => (!option.disabled ? onValueSelect(e, option.id, option?.extraValue) : null)}
                          className={`ui-select_dropdown_item_mobile${
                            singleAndSelected || multiAndSelect ? " selected" : ""
                          }${option.disabled ? " disabled" : ""}`}
                          aria-disabled={option.disabled}
                        >
                          {renderOption ? (
                            renderOption(option)
                          ) : (
                            <>
                              <p className="ui-select_dropdown_text">{option.label}</p>
                              {(singleAndSelected || multiAndSelect) && (
                                <FontAwesomeIcon className="ui-select_dropdown_check" icon={["far", "check"]} />
                              )}
                            </>
                          )}
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            </div>
          </Panel>
        </Portal>
      )}
    </div>
  );
}

function RightIcon(props: {
  clearable: boolean;
  dropdownOpen: boolean;
  clearClick: () => void;
  carrotClick: () => void;
  hideCarrots: boolean;
}) {
  const { clearable, dropdownOpen, clearClick, carrotClick, hideCarrots } = props;

  function handleClearClick(e: React.MouseEvent<SVGSVGElement, MouseEvent>) {
    e.stopPropagation();
    clearClick();
  }
  function handleCarrotClick(e: React.MouseEvent<SVGSVGElement, MouseEvent>) {
    e.stopPropagation();
    carrotClick();
  }

  if (clearable) {
    return <FontAwesomeIcon icon={faTimes} size="1x" onClick={handleClearClick} />;
  }

  return (
    <FontAwesomeIcon
      icon={dropdownOpen ? faChevronUp : faChevronDown}
      onClick={handleCarrotClick}
      className={hideCarrots ? "hidden" : ""}
    />
  );
}
